import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import {
  Autocomplete,
  Box,
  Drawer,
  Grid,
  TextField,
  Button,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

// Utils
import messages from '../../static/messages';

// Hooks
import { usePropertiesAutocomplete } from '../../hooks/useAutocomplete';

// Services
import appointmentsServices from '../../services/appointmentsServices';
import propertiesServices from '../../services/propertiesServices';
import integrationServices from '../../services/integrationServices';

// Components
import PropertyArchiveTable from '../Tables/Property/property.archive';
import PropertyLegacyProposalsTable from '../Tables/Property/property.legacyProposals';

// Styles
import { button } from '../../static/theme-styles';

// External variables
const required_fields = ['property', 'type', 'notes', 'source'];

export const appointment_times = [
  { time12: '07:00 AM', time24: '07:00' },
  { time12: '07:15 AM', time24: '07:15' },
  { time12: '07:30 AM', time24: '07:30' },
  { time12: '07:45 AM', time24: '07:45' },
  { time12: '08:00 AM', time24: '08:00' },
  { time12: '08:15 AM', time24: '08:15' },
  { time12: '08:30 AM', time24: '08:30' },
  { time12: '08:45 AM', time24: '08:45' },
  { time12: '09:00 AM', time24: '09:00' },
  { time12: '09:15 AM', time24: '09:15' },
  { time12: '09:30 AM', time24: '09:30' },
  { time12: '09:45 AM', time24: '09:45' },
  { time12: '10:00 AM', time24: '10:00' },
  { time12: '10:15 AM', time24: '10:15' },
  { time12: '10:30 AM', time24: '10:30' },
  { time12: '10:45 AM', time24: '10:45' },
  { time12: '11:00 AM', time24: '11:00' },
  { time12: '11:15 AM', time24: '11:15' },
  { time12: '11:30 AM', time24: '11:30' },
  { time12: '11:45 AM', time24: '11:45' },
  { time12: '12:00 PM', time24: '12:00' },
  { time12: '12:15 PM', time24: '12:15' },
  { time12: '12:30 PM', time24: '12:30' },
  { time12: '12:45 PM', time24: '12:45' },
  { time12: '01:00 PM', time24: '13:00' },
  { time12: '01:15 PM', time24: '13:15' },
  { time12: '01:30 PM', time24: '13:30' },
  { time12: '01:45 PM', time24: '13:45' },
  { time12: '02:00 PM', time24: '14:00' },
  { time12: '02:15 PM', time24: '14:15' },
  { time12: '02:30 PM', time24: '14:30' },
  { time12: '02:45 PM', time24: '14:45' },
  { time12: '03:00 PM', time24: '15:00' },
  { time12: '03:15 PM', time24: '15:15' },
  { time12: '03:30 PM', time24: '15:30' },
  { time12: '03:45 PM', time24: '15:45' },
  { time12: '04:00 PM', time24: '16:00' },
  { time12: '04:15 PM', time24: '16:15' },
  { time12: '04:30 PM', time24: '16:30' },
  { time12: '04:45 PM', time24: '16:45' },
  { time12: '05:00 PM', time24: '17:00' },
  { time12: '05:15 PM', time24: '17:15' },
  { time12: '05:30 PM', time24: '17:30' },
  { time12: '05:45 PM', time24: '17:45' },
  { time12: '06:00 PM', time24: '18:00' },
  { time12: '06:15 PM', time24: '18:15' },
  { time12: '06:30 PM', time24: '18:30' },
  { time12: '06:45 PM', time24: '18:45' },
  { time12: '07:00 PM', time24: '19:00' },
  { time12: '07:15 PM', time24: '19:15' },
  { time12: '07:30 PM', time24: '19:30' },
  { time12: '07:45 PM', time24: '19:45' },
  { time12: '08:00 PM', time24: '20:00' },
  { time12: '08:15 PM', time24: '20:15' },
  { time12: '08:30 PM', time24: '20:30' },
  { time12: '08:45 PM', time24: '20:45' },
  { time12: '09:00 PM', time24: '21:00' },
  { time12: '09:15 PM', time24: '21:15' },
  { time12: '09:30 PM', time24: '21:30' },
  { time12: '09:45 PM', time24: '21:45' },
  { time12: '10:00 PM', time24: '22:00' },
  { time12: '10:15 PM', time24: '22:15' },
  { time12: '10:30 PM', time24: '22:30' },
  { time12: '10:45 PM', time24: '22:45' },
  { time12: '11:00 PM', time24: '23:00' },
];

const AppointmentDrawer = (props) => {
  const { currentAppointment, setCurrentAppointment, createAppointment, technicians } = props;
  const [errors, setErrors] = useState({});
  const [types, setTypes] = useState([]);
  const [sources, setSources] = useState([]);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [loadingProperty, setLoadingProperty] = useState(false);
  const [loadingLegacyNotes, setLoadingLegacyNotes] = useState(false);

  moment.tz.setDefault('America/Los_Angeles');

  const { handleSearchProperty, propertyOptions } = usePropertiesAutocomplete(2, false);

  // Load full property details when property is selected
  const loadPropertyDetails = async (propertyId) => {
    if (!propertyId) {
      setPropertyDetails(null);
      return;
    }
    
    setLoadingProperty(true);
    try {
      const response = await propertiesServices.getPropertyById(propertyId);
      if (response.status === 200) {
        const property = response.data;
        setPropertyDetails(property);
        
        // Show notes count to dispatcher
        if (property.notes?.length > 0) {
          toast.info(`📝 Found ${property.notes.length} property note(s)`);
        }
        
        // Check if Legacy CRM Notes already exist
        const hasLegacyNotes = property.notes?.some(note => 
          note.note?.includes('[LEGACY CRM NOTES]')
        );
        
        // If no legacy notes, try to fetch them
        if (!hasLegacyNotes && property.address) {
          setLoadingLegacyNotes(true);
          try {
            const legacyResponse = await integrationServices.getLegacyPropertyNotes(property.address);
            if (legacyResponse.data.found && legacyResponse.data.notes) {
              // Save legacy notes to property
              const legacyNote = `[LEGACY CRM NOTES]\n${legacyResponse.data.notes}`;
              await propertiesServices.addPropertyNote(propertyId, legacyNote);
              
              // Reload property to get updated notes
              const updatedResponse = await propertiesServices.getPropertyById(propertyId);
              if (updatedResponse.status === 200) {
                setPropertyDetails(updatedResponse.data);
              }
              toast.success('Legacy notes imported automatically');
            }
          } catch (legacyError) {
            // Silent fail - legacy notes are optional
            console.log('No legacy notes found or error:', legacyError);
          } finally {
            setLoadingLegacyNotes(false);
          }
        }
      }
    } catch (error) {
      console.error('Error loading property details:', error);
    } finally {
      setLoadingProperty(false);
    }
  };

  // Action functions
  const resetErrors = () => {
    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
  };

  const resetQueries = () => handleSearchProperty('');

  const onPropertySelected = (event, val) => {
    event.preventDefault();
    
    // Check if val is an object with id (selected from list) or just a string (typed manually)
    if (val && typeof val === 'object' && val.id) {
      setCurrentAppointment((prevState) => ({
        ...prevState,
        property: {
          id: val.id,
        },
        isEdited: true,
      }));
      // Load full property details for archive/legacy display
      loadPropertyDetails(val.id);
    } else if (val && typeof val === 'string') {
      // User typed address manually - property will be created on save
      setCurrentAppointment((prevState) => ({
        ...prevState,
        isEdited: true,
      }));
      setPropertyDetails(null);
      toast.warning('⚠️ Please select property from the dropdown list to see notes');
    } else {
      setCurrentAppointment({
        property: {
          id: '',
        },
        isEdited: false,
      });
      setPropertyDetails(null);
    }
    resetQueries();
  };

  // Handler functions
  const handleValidate = () => {
    required_fields.forEach((field) => {
      if (currentAppointment[field] === '' || currentAppointment[field] === null || !currentAppointment[field]) {
        setErrors((prevState) => ({
          ...prevState,
          [field]: true,
        }));
      } else {
        setErrors((prevState) => ({
          ...prevState,
          [field]: false,
        }));
      }
    });
  };

  const handleChange = (event) => {
    const key = event.target?.name || 'date';
    const val = event.target?.value;

    setCurrentAppointment((prevState) => ({
      ...prevState,
      // Don't use moment.utc() for DatePicker - it shifts the date by timezone offset
      [key]: event.target?.name ? val : moment(event.$d).format('MM/DD/YYYY'),
      isEdited: true,
    }));
  };

  const handleClose = () => props.onCloseDrawer(resetErrors);

  // Async functions
  const getTypes = async () => {
    try {
      const response = await appointmentsServices.getAppointmentTypes(false);

      if (response.status === 206) setTypes(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const getSources = async () => {
    try {
      const response = await appointmentsServices.getAppointmentSources(false);

      if (response.status === 206) setSources(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    resetErrors();
  }, []);

  useEffect(() => {
    if (currentAppointment && !currentAppointment?.isEdited) {
      getTypes();
      getSources();
    }
  }, [currentAppointment]);

  useEffect(() => {
    if (currentAppointment?.isEdited) {
      !Object.values(errors).includes(true) && createAppointment();
    }
  }, [errors]);

  // console.log(currentAppointment);

  return (
    <Drawer anchor="right" open={props.drawer} onClose={handleClose}>
      <Box>
        {currentAppointment && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  Add New Appointment
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={propertyOptions}
                getOptionLabel={(option) => option?.address}
                filterOptions={(x) => x}
                onChange={onPropertySelected}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Property"
                    onChange={(e) => handleSearchProperty(e.target.value)}
                    error={errors.property}
                    helperText={errors.property && messages.errors.required_field}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="technician" error={errors.technician}>
                  Preferred Technician
                </InputLabel>
                <Select
                  labelId="technician"
                  name="technician"
                  value={currentAppointment?.technician || ''}
                  label="Preferred Technician"
                  onChange={handleChange}
                  error={errors.technician}
                >
                  {technicians?.map((tech) => (
                    <MenuItem key={tech.tech_id} value={tech.tech_id}>
                      {tech.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.technician && (
                  <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="type" error={errors.type}>
                  Appointment Type *
                </InputLabel>
                <Select
                  labelId="type"
                  name="type"
                  value={currentAppointment?.type || ''}
                  label="Appointment Type *"
                  onChange={handleChange}
                  error={errors.type}
                >
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.type && (
                  <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="source" error={errors.source}>
                  Source *
                </InputLabel>
                <Select
                  labelId="source"
                  name="source"
                  value={currentAppointment?.source || ''}
                  label="Source *"
                  onChange={handleChange}
                  required
                  error={errors.source}
                >
                  {sources.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.source && (
                  <FormHelperText sx={{ color: '#ef5350' }}>{messages.errors.required_field}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label="Date"
                  inputFormat="MM/DD/YYYY"
                  value={currentAppointment.date}
                  onChange={handleChange}
                  disablePast
                  renderInput={(params) => <TextField {...params} name="date" fullWidth error={false} />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="startAt">Start Time</InputLabel>
                    <Select
                      labelId="startAt"
                      name="startAt"
                      value={currentAppointment?.startAt}
                      label="Start Time *"
                      onChange={handleChange}
                      disabled={!currentAppointment.date}
                    >
                      {appointment_times.map((time, index) => (
                        <MenuItem key={index} value={time.time24}>
                          {time.time12}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="finishAt">End Time</InputLabel>
                    <Select
                      labelId="finishAt"
                      name="finishAt"
                      value={currentAppointment?.finishAt || ''}
                      label="End Time"
                      onChange={handleChange}
                      disabled={!currentAppointment.startAt}
                    >
                      {appointment_times.map((time, index) => (
                        <MenuItem key={index} value={time.time24}>
                          {time.time12}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Appointment Notes"
                value={currentAppointment?.notes}
                rows={4}
                onChange={handleChange}
                fullWidth
                multiline
                required
                error={errors.notes}
                helperText={errors.notes && messages.errors.required_field}
              />
            </Grid>

            {/* Property Notes Section */}
            {propertyDetails?.notes?.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  📝 Property Notes
                </Typography>
                <Box sx={{ 
                  maxHeight: 200, 
                  overflow: 'auto', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1, 
                  p: 1.5 
                }}>
                  {propertyDetails.notes.map((note, index) => (
                    <Box 
                      key={note.id || index} 
                      sx={{ 
                        mb: 1.5, 
                        pb: 1.5, 
                        borderBottom: index < propertyDetails.notes.length - 1 ? '1px solid #ddd' : 'none',
                        backgroundColor: note.note?.includes('[LEGACY CRM NOTES]') ? '#fff3e0' : 'transparent',
                        p: note.note?.includes('[LEGACY CRM NOTES]') ? 1 : 0,
                        borderRadius: 1,
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          fontWeight: note.note?.includes('[LEGACY CRM NOTES]') ? 500 : 400,
                        }}
                      >
                        {note.note}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {note.createdBy?.fullName} • {moment(note.createdAt).format('L')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            {loadingLegacyNotes && (
              <Grid item xs={12}>
                <Typography variant="body2" color="warning.main">
                  🔄 Loading legacy notes...
                </Typography>
              </Grid>
            )}

            {/* Property History Section */}
            {propertyDetails && (propertyDetails.archiveAppointments?.length > 0 || propertyDetails.legacyProposals?.length > 0) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Property History
                  </Typography>
                </Grid>

                {/* Archive Appointments */}
                {propertyDetails.archiveAppointments?.length > 0 && (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">Archive (Legacy Appointments)</Typography>
                          <Chip 
                            label={propertyDetails.archiveAppointments.length} 
                            size="small" 
                            color="warning"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1, maxHeight: 300, overflow: 'auto' }}>
                        <PropertyArchiveTable appointments={propertyDetails.archiveAppointments} />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {/* Legacy Proposals */}
                {propertyDetails.legacyProposals?.length > 0 && (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">Legacy Proposals</Typography>
                          <Chip 
                            label={propertyDetails.legacyProposals.length} 
                            size="small" 
                            color="info"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1, maxHeight: 300, overflow: 'auto' }}>
                        <PropertyLegacyProposalsTable legacyProposals={propertyDetails.legacyProposals} />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </>
            )}

            {loadingProperty && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Loading property history...
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                variant="contained"
                sx={{ ...button('primary', 'secondary'), mr: 3 }}
                onClick={handleValidate}
                disabled={!currentAppointment?.isEdited}
              >
                Create Appointment
              </Button>

              <Button variant="contained" onClick={handleClose} sx={button('secondary')}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default AppointmentDrawer;
